import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Container,
  Dropdown,
  Header,
  Icon,
  Input,
  Label,
  Loader,
  Modal,
  Segment,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
import { UserContext } from '../../context/User';

const CATEGORY_LABELS = {
  text: '对话',
  embedding: '向量',
  image: '图像',
  video: '视频',
  audio: '音频',
  '3d': '3D 生成',
};

const CATEGORY_COLORS = {
  text: 'blue',
  embedding: 'teal',
  image: 'purple',
  video: 'orange',
  audio: 'pink',
  '3d': 'green',
};

const BILLING_LABELS = {
  per_token: '按 Token 计费',
  per_call: '按次计费',
  per_unit: '按量计费',
};

function fmtPrice(v) {
  const n = Number(v);
  if (!isFinite(n)) return '-';
  if (n === 0) return '0';
  const digits = n < 0.01 ? 6 : n < 1 ? 4 : 2;
  return parseFloat(n.toFixed(digits)).toString();
}

function fmtRatio(v) {
  return parseFloat(Number(v).toFixed(4)).toString();
}

function PriceLine({ item }) {
  if (item.display_billing_type === 'per_token') {
    return (
      <div style={{ fontSize: '14px', color: '#333' }}>
        <div>
          输入：<b style={{ color: '#e03997' }}>¥{fmtPrice(item.display_input_price)}</b>
          <span style={{ color: '#999' }}> /1M tokens</span>
        </div>
        <div style={{ marginTop: '2px' }}>
          输出：<b style={{ color: '#e03997' }}>¥{fmtPrice(item.display_output_price)}</b>
          <span style={{ color: '#999' }}> /1M tokens</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ fontSize: '14px', color: '#333' }}>
      <b style={{ color: '#e03997', fontSize: '16px' }}>
        ¥{fmtPrice(item.display_unit_price)}
      </b>
      <span style={{ color: '#999' }}> /{item.display_unit}</span>
    </div>
  );
}

const ModelMarket = () => {
  const [userState] = useContext(UserContext);
  const isRoot = userState?.user?.role === 100;
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('all');
  const [provider, setProvider] = useState('all');
  const [channel, setChannel] = useState('all');
  const [selected, setSelected] = useState(null);
  // 倍率查看角色：admin(用户倍率+分销倍率) / reseller(分销倍率+客户倍率) / sub_agent(客户倍率) / none(无)
  const [viewRole, setViewRole] = useState('none');
  // 校准倍率：按模型单独设置，仅 root 可见可编辑，纯展示用途，不参与任何计费/计价逻辑
  // 存储格式：options 表 ModelMarketCalibrationRatio = JSON，如 {"seedance-2.0":1.2,"chirp-v4":2}
  const [calibrationMap, setCalibrationMap] = useState({});
  const [calibrationEditing, setCalibrationEditing] = useState({}); // {model: 输入框值}
  const [calibrationSaving, setCalibrationSaving] = useState(''); // 正在保存的模型名

  const loadMarket = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/model-market');
      const { success, data, message, view_role } = res.data || {};
      if (success) {
        setModels(data || []);
        setViewRole(view_role || 'none');
      } else {
        showError(message || '加载模型广场失败');
      }
    } catch (e) {
      showError(e.message || '加载模型广场失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMarket();
  }, []);

  // 从系统选项加载各模型的校准倍率（仅 root 需要）
  const loadCalibration = async () => {
    try {
      const res = await API.get('/api/option/');
      const { success, data } = res.data || {};
      if (success && Array.isArray(data)) {
        const opt = data.find((o) => o.key === 'ModelMarketCalibrationRatio');
        if (opt && opt.value) {
          try {
            const parsed = JSON.parse(opt.value);
            if (parsed && typeof parsed === 'object') {
              setCalibrationMap(parsed);
              return;
            }
          } catch (e) {
            // 旧版全局数字格式，忽略不迁移
          }
        }
      }
    } catch (e) {
      // 静默失败，不阻塞页面
    }
  };

  useEffect(() => {
    if (isRoot) loadCalibration();
  }, [isRoot]);

  const saveCalibration = async (modelName) => {
    const raw = calibrationEditing[modelName];
    if (raw === undefined || String(raw).trim() === '') {
      showError('请输入校准倍率');
      return;
    }
    const v = parseFloat(raw);
    if (!isFinite(v) || v <= 0) {
      showError('校准倍率必须是大于 0 的数字');
      return;
    }
    setCalibrationSaving(modelName);
    try {
      const nextMap = { ...calibrationMap, [modelName]: v };
      const res = await API.put('/api/option/', {
        key: 'ModelMarketCalibrationRatio',
        value: JSON.stringify(nextMap),
      });
      const { success, message } = res.data || {};
      if (success) {
        setCalibrationMap(nextMap);
        showSuccess(`模型 ${modelName} 的校准倍率已保存`);
      } else {
        showError(message || '保存失败');
      }
    } catch (e) {
      showError(e.message || '保存失败');
    }
    setCalibrationSaving('');
  };

  const providers = useMemo(() => {
    const set = new Set(models.map((m) => m.provider));
    return Array.from(set).sort();
  }, [models]);

  const channels = useMemo(() => {
    const set = new Set();
    models.forEach((m) => (m.channels || []).forEach((ch) => set.add(ch)));
    return Array.from(set).sort();
  }, [models]);

  const filtered = useMemo(() => {
    return models.filter((m) => {
      if (category !== 'all' && m.category !== category) return false;
      if (provider !== 'all' && m.provider !== provider) return false;
      if (channel !== 'all' && !(m.channels || []).includes(channel)) return false;
      if (keyword && !m.model.toLowerCase().includes(keyword.toLowerCase()))
        return false;
      return true;
    });
  }, [models, category, provider, channel, keyword]);

  const copyCurl = async () => {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected.curl);
      showSuccess('curl 命令已复制');
    } catch (e) {
      showError('复制失败，请手动选择复制');
    }
  };

  const categoryOptions = [
    { key: 'all', value: 'all', text: '全部类型' },
    ...Object.keys(CATEGORY_LABELS).map((k) => ({
      key: k,
      value: k,
      text: CATEGORY_LABELS[k],
    })),
  ];

  const providerOptions = [
    { key: 'all', value: 'all', text: '全部厂商' },
    ...providers.map((p) => ({ key: p, value: p, text: p })),
  ];

  const channelOptions = [
    { key: 'all', value: 'all', text: '全部渠道' },
    ...channels.map((ch) => ({ key: ch, value: ch, text: ch })),
  ];

  return (
    <Container style={{ maxWidth: '1300px', padding: '20px' }}>
      <Header as='h2' style={{ marginBottom: '4px' }}>
        <Icon name='cubes' />
        <Header.Content>
          模型广场
          <Header.Subheader>
            {viewRole === 'admin'
              ? '以下为基础售价（未乘倍率），实际扣费 = 售价 × 用户倍率；点击模型查看调用示例'
              : viewRole === 'reseller'
              ? '以下为基础售价（未乘倍率），实际扣费 = 售价 × 分销倍率；点击模型查看调用示例'
              : viewRole === 'sub_agent'
              ? '以下为基础售价（未乘倍率），实际扣费 = 售价 × 分销倍率；点击模型查看调用示例'
              : '以下为基础售价；点击模型查看调用示例'}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Segment style={{ marginTop: '16px' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <Input
            icon='search'
            placeholder='搜索模型名称...'
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ minWidth: '260px' }}
          />
          <Dropdown
            selection
            options={categoryOptions}
            value={category}
            onChange={(e, { value }) => setCategory(value)}
          />
          <Dropdown
            selection
            options={providerOptions}
            value={provider}
            onChange={(e, { value }) => setProvider(value)}
          />
          <Dropdown
            selection
            options={channelOptions}
            value={channel}
            onChange={(e, { value }) => setChannel(value)}
          />
          <span style={{ color: '#999', fontSize: '13px' }}>
            共 {filtered.length} 个模型
          </span>
        </div>
        {isRoot && (
          <div style={{ marginTop: '8px', color: '#999', fontSize: '12px' }}>
            校准倍率按模型单独设置（在每个模型卡片上编辑），仅展示记录用途，不参与页面价格与实际扣费计算
          </div>
        )}
      </Segment>

      {loading ? (
        <Segment placeholder style={{ minHeight: '300px' }}>
          <Loader active inline='centered' content='加载中...' />
        </Segment>
      ) : (
        <Card.Group itemsPerRow={4} stackable style={{ marginTop: '8px' }}>
          {filtered.map((item) => (
            <Card
              key={`${item.model}:${(item.channels || [])[0] || ''}`}
              onClick={() => setSelected(item)}
              style={{ cursor: 'pointer' }}
            >
              <Card.Content>
                <Card.Header
                  style={{
                    fontSize: '15px',
                    wordBreak: 'break-all',
                    lineHeight: '1.4',
                  }}
                >
                  {item.model}
                </Card.Header>
                <Card.Meta style={{ marginTop: '6px' }}>
                  <Label size='mini' basic color={CATEGORY_COLORS[item.category]}>
                    {CATEGORY_LABELS[item.category] || item.category}
                  </Label>
                  <Label size='mini' basic style={{ marginLeft: '4px' }}>
                    {item.provider}
                  </Label>
                  {(item.channels || []).map((ch) => (
                    <Label
                      key={ch}
                      size='mini'
                      color='grey'
                      style={{ marginLeft: '4px', marginTop: '4px' }}
                    >
                      <Icon name='plug' style={{ marginRight: '2px' }} />
                      {ch}
                    </Label>
                  ))}
                </Card.Meta>
              </Card.Content>
              <Card.Content>
                <PriceLine item={item} />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  {BILLING_LABELS[item.display_billing_type] || ''}
                  {viewRole === 'admin' && item.display_user_ratio > 0 && (
                    <> · 用户倍率 {fmtRatio(item.display_user_ratio)}</>
                  )}
                  {(viewRole === 'reseller' || viewRole === 'sub_agent') && item.display_reseller_ratio > 0 && (
                    <> · 分销倍率 {fmtRatio(item.display_reseller_ratio)}</>
                  )}
                </div>
                {isRoot && (
                  <div
                    style={{
                      marginTop: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      校准倍率
                    </span>
                    <Input
                      size='mini'
                      style={{ width: '80px' }}
                      value={
                        calibrationEditing[item.model] !== undefined
                          ? calibrationEditing[item.model]
                          : calibrationMap[item.model] !== undefined
                          ? String(calibrationMap[item.model])
                          : ''
                      }
                      onChange={(e) =>
                        setCalibrationEditing({
                          ...calibrationEditing,
                          [item.model]: e.target.value,
                        })
                      }
                      placeholder='未设置'
                    />
                    <Button
                      size='mini'
                      primary
                      loading={calibrationSaving === item.model}
                      disabled={calibrationSaving !== '' && calibrationSaving !== item.model}
                      onClick={() => saveCalibration(item.model)}
                    >
                      保存
                    </Button>
                  </div>
                )}
              </Card.Content>
            </Card>
          ))}
        </Card.Group>
      )}

      {!loading && filtered.length === 0 && (
        <Segment placeholder style={{ minHeight: '200px' }}>
          <Header icon>
            <Icon name='search' />
            没有匹配的模型
          </Header>
        </Segment>
      )}

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size='large'
        closeIcon
      >
        {selected && (
          <>
            <Modal.Header>
              {selected.model}
              <Label
                size='small'
                color={CATEGORY_COLORS[selected.category]}
                style={{ marginLeft: '10px', verticalAlign: 'middle' }}
              >
                {CATEGORY_LABELS[selected.category] || selected.category}
              </Label>
              <Label
                size='small'
                basic
                style={{ marginLeft: '4px', verticalAlign: 'middle' }}
              >
                {selected.provider}
              </Label>
              {(selected.channels || []).map((ch) => (
                <Label
                  key={ch}
                  size='small'
                  color='grey'
                  style={{ marginLeft: '4px', verticalAlign: 'middle' }}
                >
                  <Icon name='plug' style={{ marginRight: '2px' }} />
                  {ch}
                </Label>
              ))}
            </Modal.Header>
            <Modal.Content>
              <Header as='h4'>
                计费信息
                {viewRole === 'admin' ? '（用户倍率 / 分销倍率）'
                  : (viewRole === 'reseller' || viewRole === 'sub_agent') ? '（分销倍率）' : ''}
              </Header>
              <Table celled compact size='small'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>渠道</Table.HeaderCell>
                    <Table.HeaderCell>计费模式</Table.HeaderCell>
                    <Table.HeaderCell>售价</Table.HeaderCell>
                    {viewRole === 'admin' && (
                      <>
                        <Table.HeaderCell>用户倍率</Table.HeaderCell>
                        <Table.HeaderCell>分销倍率</Table.HeaderCell>
                      </>
                    )}
                    {(viewRole === 'reseller' || viewRole === 'sub_agent') && (
                      <Table.HeaderCell>分销倍率</Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {selected.prices.map((p) => (
                    <Table.Row key={`${p.channel_id}-${p.billing_type}`}>
                      <Table.Cell>{p.channel_name}</Table.Cell>
                      <Table.Cell>
                        {BILLING_LABELS[p.billing_type] || p.billing_type}
                      </Table.Cell>
                      <Table.Cell>
                        {p.billing_type === 'per_token' ? (
                          <span>
                            输入 ¥{fmtPrice(p.input_price)} · 输出 ¥
                            {fmtPrice(p.output_price)}{' '}
                            <span style={{ color: '#999' }}>/1M tokens</span>
                          </span>
                        ) : (
                          <span>
                            ¥{fmtPrice(p.unit_price)}{' '}
                            <span style={{ color: '#999' }}>/{p.unit}</span>
                          </span>
                        )}
                      </Table.Cell>
                      {viewRole === 'admin' && (
                        <>
                          <Table.Cell>{p.user_ratio > 0 ? fmtRatio(p.user_ratio) : '-'}</Table.Cell>
                          <Table.Cell>{p.fenxiao_ratio > 0 ? fmtRatio(p.fenxiao_ratio) : '-'}</Table.Cell>
                        </>
                      )}
                      {(viewRole === 'reseller' || viewRole === 'sub_agent') && (
                        <Table.Cell>{p.reseller_ratio > 0 ? fmtRatio(p.reseller_ratio) : '-'}</Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              {isRoot && (
                <p style={{ color: '#999', fontSize: '13px' }}>
                  校准倍率：
                  {calibrationMap[selected.model] !== undefined
                    ? calibrationMap[selected.model]
                    : '未设置'}
                  （仅展示记录，不参与计价）
                </p>
              )}

              <Header as='h4'>
                调用端点
                <Header.Subheader>
                  {selected.endpoint} · 认证方式：Bearer Token
                </Header.Subheader>
              </Header>

              <Header as='h4'>curl 请求示例</Header>
              <Segment style={{ position: 'relative', background: '#2d2d2d' }}>
                <Button
                  size='mini'
                  inverted
                  icon='copy'
                  content='复制'
                  style={{ position: 'absolute', top: '8px', right: '8px' }}
                  onClick={copyCurl}
                />
                <pre
                  style={{
                    margin: 0,
                    color: '#f8f8f2',
                    fontSize: '13px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontFamily:
                      "Consolas, 'Courier New', monospace",
                  }}
                >
                  {selected.curl}
                </pre>
              </Segment>
              <p style={{ color: '#999', fontSize: '13px' }}>
                <Icon name='info circle' />
                请将 sk-your-api-key
                替换为你的真实 API Key（在「Token 管理」中创建）。
                {selected.category === 'video' &&
                  ' 视频任务提交后为异步生成，请通过返回结果中的任务查询接口轮询状态。'}
              </p>
            </Modal.Content>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default ModelMarket;
